// Drag-and-drop file upload box.
//
// The real <input type="file"> is stretched invisibly over the whole box, so clicking anywhere
// opens the file picker and dropping files onto the box lands them on the input natively. The
// form therefore posts exactly as a plain file input would. This script highlights the box while
// dragging, shows what's selected, and lets the selection be cleared.
//
// A file input replaces its selection each time files are picked or dropped, so this script keeps
// its own list and writes the combined list back to the input. Picking again adds to the
// selection (a file with the same name replaces the earlier one); only the clear button empties it.
//
// Files already uploaded on an earlier visit can be listed alongside the box. Each one's toggle
// marks it for removal by enabling a hidden input that posts its name; the server removes it when
// the form is submitted, so it can be undone until then. If every uploaded file is marked for
// removal, the file input becomes required again.
//
// Markup:
//   <div data-file-dropzone data-max-file-size-mb="25">  <!-- size limit optional -->
//       <div class="file-dropzone">
//           <input type="file" class="file-dropzone__input" ...>
//           ...
//           <div class="file-dropzone__summary" data-file-dropzone-summary hidden>
//               <span data-file-dropzone-text></span>
//               <button type="button" data-file-dropzone-clear>...</button>
//           </div>
//       </div>
//       <p data-file-dropzone-error hidden></p>  <!-- optional, shows files rejected for size -->
//       <!-- optional, one per uploaded file -->
//       <li data-file-dropzone-existing>
//           <button type="button" data-file-dropzone-existing-toggle data-remove-label="..." data-undo-label="..."><i class="bi bi-x-lg"></i></button>
//           <input type="hidden" name="..." value="..." disabled>
//       </li>
//   </div>

export function initFileDropzones(): void {
    const dropzones = document.querySelectorAll<HTMLElement>('[data-file-dropzone]');

    dropzones.forEach((dropzone) => {
        // prevent double init
        if (dropzone.dataset.fileDropzoneInitialised === "1") return;
        dropzone.dataset.fileDropzoneInitialised = "1";

        const input = dropzone.querySelector<HTMLInputElement>('input[type="file"]');
        const box = input?.closest<HTMLElement>('.file-dropzone');
        const summary = dropzone.querySelector<HTMLElement>('[data-file-dropzone-summary]');
        const text = dropzone.querySelector<HTMLElement>('[data-file-dropzone-text]');
        const clear = dropzone.querySelector<HTMLButtonElement>('[data-file-dropzone-clear]');
        const error = dropzone.querySelector<HTMLElement>('[data-file-dropzone-error]');

        if (!input || !box || !summary || !text || !clear) return;

        // Optional; the server enforces the same limit.
        const maxFileSizeMb = Number(dropzone.dataset.maxFileSizeMb) || 0;

        const showError = (message: string) => {
            if (!error) return;

            error.textContent = message;
            error.hidden = message === '';
        };

        // The browser may restore a selection on back/forward navigation.
        let selectedFiles: File[] = Array.from(input.files ?? []);

        const updateSummary = () => {
            const count = selectedFiles.length;

            text.textContent = count === 1 ? selectedFiles[0].name : `${count} files selected`;
            summary.hidden = count === 0;
        };

        const setSelectedFiles = (files: File[]) => {
            selectedFiles = files;

            // Setting input.files doesn't raise a change event, so this doesn't re-enter the handler.
            const dataTransfer = new DataTransfer();
            files.forEach((file) => dataTransfer.items.add(file));
            input.files = dataTransfer.files;

            updateSummary();
        };

        const addFiles = (newFiles: File[]) => {
            const newNames = new Set(newFiles.map((file) => file.name.toLowerCase()));
            const kept = selectedFiles.filter((file) => !newNames.has(file.name.toLowerCase()));

            setSelectedFiles([...kept, ...newFiles]);
        };

        ['dragenter', 'dragover'].forEach((eventName) =>
            input.addEventListener(eventName, () => box.classList.add('is-active')));

        ['dragleave', 'dragend', 'drop'].forEach((eventName) =>
            input.addEventListener(eventName, () => box.classList.remove('is-active')));

        // input.files now holds only the latest pick/drop. Cancelling the picker can also fire this
        // with nothing picked, which shouldn't clear what's already selected.
        input.addEventListener('change', () => {
            const picked = Array.from(input.files ?? []);

            if (picked.length === 0) {
                setSelectedFiles(selectedFiles);
                return;
            }

            const tooLarge = maxFileSizeMb > 0
                ? picked.filter((file) => file.size > maxFileSizeMb * 1024 * 1024)
                : [];

            showError(tooLarge.length > 0
                ? `${tooLarge.map((file) => file.name).join(', ')} ${tooLarge.length === 1 ? 'is' : 'are'} larger than ${maxFileSizeMb}MB and ${tooLarge.length === 1 ? "wasn't" : "weren't"} added.`
                : '');

            addFiles(picked.filter((file) => !tooLarge.includes(file)));
        });

        clear.addEventListener('click', () => {
            setSelectedFiles([]);
            showError('');
            input.focus();
        });

        initExistingFiles(dropzone, input);

        updateSummary();
    });
}

function initExistingFiles(dropzone: HTMLElement, input: HTMLInputElement): void {
    const existingFiles = Array.from(dropzone.querySelectorAll<HTMLElement>('[data-file-dropzone-existing]'));

    if (existingFiles.length === 0) return;

    const isRemoved = (existingFile: HTMLElement) =>
        existingFile.querySelector<HTMLInputElement>('input[type="hidden"]')?.disabled === false;

    existingFiles.forEach((existingFile) => {
        const toggle = existingFile.querySelector<HTMLButtonElement>('[data-file-dropzone-existing-toggle]');
        const removedInput = existingFile.querySelector<HTMLInputElement>('input[type="hidden"]');
        const icon = toggle?.querySelector<HTMLElement>('i');

        if (!toggle || !removedInput || !icon) return;

        toggle.addEventListener('click', () => {
            const removed = !isRemoved(existingFile);

            removedInput.disabled = !removed;
            existingFile.classList.toggle('is-removed', removed);
            icon.className = removed ? 'bi bi-arrow-counterclockwise' : 'bi bi-x-lg';
            toggle.setAttribute('aria-label', (removed ? toggle.dataset.undoLabel : toggle.dataset.removeLabel) ?? '');

            // Something must still be uploaded once the removals are applied.
            input.required = existingFiles.every(isRemoved);
        });
    });
}
